import { useState, useCallback, useMemo } from 'react';
import { ref, get } from 'firebase/database';
import { database } from '../../../../config/firebase';

/**
 * QuizSectionの結果管理を担当するカスタムフック
 * 
 * @param {boolean} isTeacher - 教師かどうかのフラグ
 * @param {string} studentId - 生徒ID
 * @param {Object} currentUser - 現在のユーザー情報  
 * @param {Object} selectedStudent - 選択された生徒情報
 * @returns {Object} 結果管理に関する状態とメソッド
 */
const useQuizResults = (isTeacher, studentId, currentUser, selectedStudent) => {
  // 状態管理
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [resultsData, setResultsData] = useState(null);
  const [resultsTitle, setResultsTitle] = useState('');
  const [resultsError, setResultsError] = useState(null);

  // メモ化した結果データ
  const memoizedResults = useMemo(() => resultsData, [resultsData]);

  // 結果表示処理
  const handleViewResults = useCallback(async (contentId, contentTitle) => {
    try {
      setResultsError(null);
      const targetStudentId = isTeacher ? selectedStudent?.id : currentUser?.id;
      
      if (!targetStudentId) {
        setResultsError('生徒情報が見つかりません。');
        return;
      }

      const resultsRef = ref(database, `results/${targetStudentId}/${contentId}`);
      const snapshot = await get(resultsRef);
      const data = snapshot.val();

      if (data) {
        // 一度だけソートを行う
        const sortedResults = Object.values(data)
          .map(result => ({
            ...result,
            timestamp: result.timestamp || Date.now(),
            attemptNumber: result.attemptNumber || 1
          }))
          .sort((a, b) => b.timestamp - a.timestamp);

        setResultsData(sortedResults);
        setResultsTitle(contentTitle);
        setShowResultsModal(true);
      } else {
        // データがない場合も空配列でモーダルを表示
        setResultsData([]);
        setResultsTitle(contentTitle);
        setShowResultsModal(true);
      }
    } catch (error) {
      console.error('結果の取得に失敗しました:', error);
      setResultsError('結果の取得に失敗しました。');
    }
  }, [isTeacher, selectedStudent?.id, currentUser?.id]);

  // 結果モーダルを閉じる
  const closeResultsModal = useCallback(() => {
    setShowResultsModal(false);
    setResultsData(null);
    setResultsTitle('');
    setResultsError(null);
  }, []);

  // 結果データの統計情報を計算
  const getResultsStatistics = useCallback((results) => {
    if (!results || results.length === 0) {
      return {
        totalAttempts: 0,
        averageScore: 0,
        bestScore: 0,
        latestScore: 0,
        improvement: 0
      };
    }

    const totalAttempts = results.length;
    const scores = results.map(result => result.correctRate || 0);
    const averageScore = scores.reduce((sum, score) => sum + score, 0) / totalAttempts;
    const bestScore = Math.max(...scores);
    const latestScore = results[0]?.correctRate || 0; // 最新（最初の要素）
    
    // 改善度計算（最初の挑戦と最新の挑戦の差）
    const firstScore = results[results.length - 1]?.correctRate || 0;
    const improvement = latestScore - firstScore;

    return {
      totalAttempts,
      averageScore: Math.round(averageScore * 10) / 10,
      bestScore: Math.round(bestScore * 10) / 10,
      latestScore: Math.round(latestScore * 10) / 10,
      improvement: Math.round(improvement * 10) / 10
    };
  }, []);

  // 特定の結果の詳細を取得
  const getResultDetails = useCallback((result) => {
    if (!result) return null;

    return {
      attemptNumber: result.attemptNumber,
      timestamp: result.timestamp,
      correctRate: result.correctRate,
      correctCount: result.correctCount,
      total: result.total,
      elapsedTime: result.elapsedTime,
      isTimeOver: result.isTimeOver,
      detailedResults: result.detailedResults || []
    };
  }, []);

  // 経過時間のフォーマット
  const formatElapsedTime = useCallback((elapsedTime) => {
    if (!elapsedTime) return '0秒';
    
    const seconds = Math.floor(elapsedTime / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes > 0) {
      return `${minutes}分${remainingSeconds}秒`;
    } else {
      return `${remainingSeconds}秒`;
    }
  }, []);

  // タイムスタンプのフォーマット
  const formatTimestamp = useCallback((timestamp) => {
    if (!timestamp) return '不明';
    
    const date = new Date(timestamp);
    const options = {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    };
    return date.toLocaleString('ja-JP', options);
  }, []);

  // エラークリア
  const clearResultsError = useCallback(() => {
    setResultsError(null);
  }, []);

  // 結果の有無チェック
  const hasResults = useCallback((results) => {
    return results && Array.isArray(results) && results.length > 0;
  }, []);

  return {
    // 状態
    showResultsModal,
    resultsData,
    resultsTitle,
    resultsError,
    memoizedResults,

    // メソッド
    handleViewResults,
    closeResultsModal,
    getResultsStatistics,
    getResultDetails,
    formatElapsedTime,
    formatTimestamp,
    clearResultsError,
    hasResults
  };
};

export default useQuizResults; 